/**
 * SmartDropdown Hook
 *
 * Uses floating-ui's flip middleware to automatically position dropdowns:
 * - Default: below the trigger element
 * - When near bottom: automatically flips above the trigger
 *
 * Usage: Add phx-hook="SmartDropdown" to the container element
 * - Trigger element should have data-anchor attribute
 * - Dropdown element should have data-floating attribute
 */

import { autoUpdate, computePosition, flip, offset } from '@floating-ui/dom'
import { Hook, makeHook } from './Hook'

export default makeHook(
  class SmartDropdown extends Hook {
    private floatingCleanup?: () => void

    mounted() {
      this.setupFloatingUI()
    }

    updated() {
      this.setupFloatingUI()
    }

    destroyed() {
      this.cleanup()
    }

    private setupFloatingUI() {
      const floating = this.el.querySelector<HTMLElement>('[data-floating]')
      const anchor = this.el.querySelector<HTMLElement>('[data-anchor]')

      if (!floating || !anchor) {
        // Clean up if elements don't exist
        this.cleanup()
        return
      }

      // Clean up any existing setup
      this.cleanup()

      // Reset floating element styles to prevent stacking
      floating.style.cssText = ''
      floating.className = floating.className // Preserve classes

      // Immediately set the floating element out of document flow
      floating.style.position = 'absolute'
      floating.style.zIndex = '50'
      floating.style.visibility = 'hidden' // Hide until positioned

      const updatePosition = async () => {
        const { x, y } = await computePosition(anchor, floating, {
          placement: 'bottom-start',
          strategy: 'absolute', // Use absolute positioning relative to container
          middleware: [
            offset(4), // 4px gap between trigger and dropdown
            flip({
              padding: 8,
              // Only flip vertically (top/bottom), no horizontal adjustments
              mainAxis: true,
              crossAxis: false,
            }),
          ],
        })

        Object.assign(floating.style, {
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          zIndex: '50',
          visibility: 'visible', // Show after positioning
          // Ensure it's completely out of document flow
          width: 'max-content',
          maxWidth: '320px', // w-80 equivalent
          margin: '0', // Override any margin
          // Don't override transform as it can affect shadows
        })
      }

      updatePosition()
      this.floatingCleanup = autoUpdate(anchor, floating, updatePosition)
    }

    private cleanup() {
      if (this.floatingCleanup) {
        this.floatingCleanup()
        this.floatingCleanup = undefined
      }
    }
  }
)
